// src/components/common/AdjuntosPanel.tsx
import { useRef, useState, useCallback, useEffect } from 'react';
import { Button, Spinner, toast } from '@/components/ui';
import {
  Upload, File, Download, Trash2, FileText, Image as ImageIcon,
  FileSpreadsheet, Film, Music, Archive, X, Paperclip,
} from 'lucide-react';
import { formatDateTime, cn } from '@/lib/utils';
import { useArchivos, useSubirMultiplesArchivos, useEliminarArchivo } from '@/hooks/queries/useArchivos';
import { archivosService } from '@/services/archivos';
import { useAuthStore } from '@/stores/authStore';
import type { ArchivoContexto, Archivo, TipoArchivo } from '@/types';

// --- Validation constants (match backend) ---
const EXTENSIONES_IMAGEN = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'ico'];
const EXTENSIONES_DOCUMENTO = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'csv', 'json', 'xml', 'md', 'rtf'];
const EXTENSIONES_VIDEO = ['mp4', 'webm', 'mov', 'avi', 'mkv'];
const EXTENSIONES_AUDIO = ['mp3', 'wav', 'ogg', 'flac', 'm4a'];
const EXTENSIONES_COMPRIMIDO = ['zip', 'rar', '7z', 'tar', 'gz', 'bz2'];
const EXTENSIONES_BLOQUEADAS = ['exe', 'bat', 'sh', 'cmd', 'msi', 'dll'];

const SIZE_LIMITS: Record<string, number> = {
  imagen: 10 * 1024 * 1024,
  documento: 25 * 1024 * 1024,
  video: 100 * 1024 * 1024,
  comprimido: 50 * 1024 * 1024,
};

function getExtension(filename: string): string {
  return filename.split('.').pop()?.toLowerCase() || '';
}

function detectTipo(ext: string): string {
  if (EXTENSIONES_IMAGEN.includes(ext)) return 'imagen';
  if (EXTENSIONES_DOCUMENTO.includes(ext)) return 'documento';
  if (EXTENSIONES_VIDEO.includes(ext)) return 'video';
  if (EXTENSIONES_AUDIO.includes(ext)) return 'audio';
  if (EXTENSIONES_COMPRIMIDO.includes(ext)) return 'comprimido';
  return 'otro';
}

function validateFile(file: File): string | null {
  const ext = getExtension(file.name);
  if (EXTENSIONES_BLOQUEADAS.includes(ext)) {
    return `Tipo bloqueado: .${ext}`;
  }
  const tipo = detectTipo(ext);
  const limit = SIZE_LIMITS[tipo];
  if (limit && file.size > limit) {
    return `Excede ${formatBytes(limit)} (${formatBytes(file.size)})`;
  }
  return null;
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

function getIcono(tipo: TipoArchivo, mimeType?: string) {
  switch (tipo) {
    case 'IMAGEN':
      return <ImageIcon className="h-8 w-8 text-green-500" />;
    case 'VIDEO':
      return <Film className="h-8 w-8 text-purple-500" />;
    case 'AUDIO':
      return <Music className="h-8 w-8 text-orange-500" />;
    case 'COMPRIMIDO':
      return <Archive className="h-8 w-8 text-yellow-600" />;
    case 'DOCUMENTO':
      if (mimeType === 'application/pdf') return <FileText className="h-8 w-8 text-red-500" />;
      if (mimeType?.includes('spreadsheet') || mimeType?.includes('excel'))
        return <FileSpreadsheet className="h-8 w-8 text-green-600" />;
      return <FileText className="h-8 w-8 text-blue-500" />;
    default:
      return <File className="h-8 w-8 text-gray-400" />;
  }
}

// --- Component Props ---
interface AdjuntosPanelProps {
  contexto: ArchivoContexto;
  entidadId: string;
  readonly?: boolean;
  compact?: boolean;
}

// --- Main Component ---
export function AdjuntosPanel({ contexto, entidadId, readonly = false, compact = false }: AdjuntosPanelProps) {
  const { data, isLoading, isError } = useArchivos(contexto, entidadId);
  const subirMutation = useSubirMultiplesArchivos(contexto);
  const eliminarMutation = useEliminarArchivo(contexto);
  const { user } = useAuthStore();
  const inputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);

  const [isDragging, setIsDragging] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [pendingPasteFiles, setPendingPasteFiles] = useState<File[]>([]);
  const [pastePreviewUrls, setPastePreviewUrls] = useState<string[]>([]);

  const archivos = data?.archivos ?? [];

  // --- File processing ---
  const processFiles = useCallback((files: File[]) => {
    const valid: File[] = [];
    for (const file of files) {
      const error = validateFile(file);
      if (error) {
        toast.error(`${file.name}: ${error}`);
      } else {
        valid.push(file);
      }
    }
    if (valid.length > 0) {
      subirMutation.mutate({ entidadId, files: valid });
    }
  }, [entidadId, subirMutation]);

  // --- File input handler ---
  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length) return;
    processFiles(Array.from(files));
    if (inputRef.current) inputRef.current.value = '';
  }, [processFiles]);

  // --- Drag & drop ---
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only leave if we're leaving the drop zone (not entering a child)
    if (e.currentTarget === e.target) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) processFiles(files);
  }, [processFiles]);

  // --- Clipboard paste ---
  useEffect(() => {
    if (readonly) return;

    const handlePaste = (e: ClipboardEvent) => {
      const items = Array.from(e.clipboardData?.items || []);
      const fileItems = items.filter(item => item.kind === 'file');
      if (fileItems.length === 0) return;

      // Don't intercept if user is in a text input and clipboard also has text
      const active = document.activeElement;
      const isInTextInput = active?.tagName === 'INPUT'
        || active?.tagName === 'TEXTAREA'
        || active?.getAttribute('contenteditable') === 'true';
      const hasText = items.some(item => item.type.startsWith('text/'));
      if (isInTextInput && hasText) return;

      e.preventDefault();

      const files = fileItems
        .map(item => item.getAsFile())
        .filter((f): f is File => f !== null)
        .map((file, i) => {
          // Rename clipboard images to something meaningful
          if (file.type.startsWith('image/') && (file.name === 'image.png' || !file.name)) {
            const now = new Date();
            const ts = now.toISOString().replace(/[-:T]/g, '').slice(0, 15);
            const ext = file.type.split('/')[1] || 'png';
            return new globalThis.File([file], `captura_${ts}${i > 0 ? `_${i}` : ''}.${ext}`, { type: file.type });
          }
          return file;
        });

      if (files.length === 0) return;

      // For images, show preview before uploading
      const hasImages = files.some(f => f.type.startsWith('image/'));
      if (hasImages) {
        setPendingPasteFiles(files);
        const urls = files.map(f => URL.createObjectURL(f));
        setPastePreviewUrls(urls);
      } else {
        processFiles(files);
      }
    };

    // Attach to the closest Sheet/dialog container so it captures pastes within the drawer
    const container = dropZoneRef.current?.closest('[role="dialog"]') || document;
    container.addEventListener('paste', handlePaste as EventListener);
    return () => container.removeEventListener('paste', handlePaste as EventListener);
  }, [readonly, processFiles]);

  // Cleanup preview URLs
  useEffect(() => {
    return () => pastePreviewUrls.forEach(url => URL.revokeObjectURL(url));
  }, [pastePreviewUrls]);

  const clearPaste = useCallback(() => {
    pastePreviewUrls.forEach(url => URL.revokeObjectURL(url));
    setPendingPasteFiles([]);
    setPastePreviewUrls([]);
  }, [pastePreviewUrls]);

  const confirmPaste = useCallback(() => {
    processFiles(pendingPasteFiles);
    clearPaste();
  }, [pendingPasteFiles, processFiles, clearPaste]);

  // --- Download ---
  const handleDescargar = async (archivo: Archivo) => {
    try {
      const blob = await archivosService.descargar(archivo.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', archivo.nombre_original);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error al descargar archivo');
    }
  };

  // --- Delete ---
  const handleEliminar = (archivo: Archivo) => {
    if (window.confirm(`¿Eliminar "${archivo.nombre_original}"?`)) {
      eliminarMutation.mutate({ archivoId: archivo.id, entidadId });
    }
  };

  const hasThumbnail = (archivo: Archivo) =>
    archivo.tipo === 'IMAGEN' && !!archivo.url_miniatura;

  // --- Lightbox ---
  const openLightbox = async (archivo: Archivo) => {
    try {
      const blob = await archivosService.descargar(archivo.id);
      const url = URL.createObjectURL(blob);
      setLightboxUrl(url);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error al cargar imagen');
    }
  };

  const closeLightbox = useCallback(() => {
    if (lightboxUrl) URL.revokeObjectURL(lightboxUrl);
    setLightboxUrl(null);
  }, [lightboxUrl]);

  // Escape key closes lightbox
  useEffect(() => {
    if (!lightboxUrl) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeLightbox();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [lightboxUrl, closeLightbox]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-4">
        <Spinner size="sm" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-center text-destructive text-sm py-2">
        Error al cargar adjuntos
      </div>
    );
  }

  const isUploading = subirMutation.isPending;

  return (
    <div ref={dropZoneRef} className="space-y-3">
      {/* Upload area (drag & drop + button) */}
      {!readonly && (
        <div
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          className={cn(
            'relative border-2 border-dashed rounded-lg transition-colors',
            compact ? 'p-4' : 'p-6',
            isDragging
              ? 'border-primary bg-primary/5'
              : 'border-muted-foreground/25 hover:border-muted-foreground/40',
          )}
        >
          <input
            ref={inputRef}
            type="file"
            multiple
            onChange={handleFileChange}
            className="hidden"
            id={`file-upload-${contexto}-${entidadId}`}
          />

          {isDragging ? (
            <div className="text-center">
              <Upload className="h-8 w-8 mx-auto mb-2 text-primary" />
              <p className="text-sm font-medium text-primary">Soltar archivos aqui</p>
            </div>
          ) : (
            <div className="text-center">
              <Paperclip className="h-6 w-6 mx-auto mb-1.5 text-muted-foreground" />
              <label
                htmlFor={`file-upload-${contexto}-${entidadId}`}
                className="cursor-pointer"
              >
                <span className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  Arrastra archivos o <span className="text-primary font-medium">haz clic para seleccionar</span>
                </span>
              </label>
              <p className="text-xs text-muted-foreground/60 mt-1">
                Imagenes 10 MB · Documentos 25 MB · Videos 100 MB · Ctrl+V para pegar capturas
              </p>
            </div>
          )}
        </div>
      )}

      {/* Upload progress */}
      {isUploading && (
        <div className="flex items-center gap-2 text-sm text-primary px-1">
          <Spinner size="sm" />
          <span>Subiendo archivo(s)...</span>
        </div>
      )}

      {/* Paste preview */}
      {pendingPasteFiles.length > 0 && (
        <div className="border rounded-lg p-3 bg-muted/30 space-y-2">
          <p className="text-sm font-medium">Pegar {pendingPasteFiles.length} imagen(es)?</p>
          <div className="flex gap-2 flex-wrap">
            {pastePreviewUrls.map((url, i) => (
              <img
                key={i}
                src={url}
                alt={pendingPasteFiles[i]?.name}
                className="h-16 w-16 object-cover rounded border"
              />
            ))}
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={confirmPaste}>
              <Upload className="h-3.5 w-3.5 mr-1" />
              Subir
            </Button>
            <Button size="sm" variant="ghost" onClick={clearPaste}>
              Cancelar
            </Button>
          </div>
        </div>
      )}

      {/* File list */}
      {archivos.length > 0 && (
        <div className="space-y-1.5">
          {archivos.map((archivo) => (
            <ArchivoItem
              key={archivo.id}
              archivo={archivo}
              compact={compact}
              canDelete={!readonly && archivo.subido_por?.id === user?.id}
              onDescargar={handleDescargar}
              onEliminar={handleEliminar}
              onPreview={archivo.tipo === 'IMAGEN' ? () => openLightbox(archivo) : undefined}
              showThumbnail={hasThumbnail(archivo)}
            />
          ))}
        </div>
      )}

      {archivos.length === 0 && !isUploading && (
        <p className="text-center text-muted-foreground text-sm py-2">
          Sin adjuntos
        </p>
      )}

      {/* Lightbox */}
      {lightboxUrl && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80"
          onClick={closeLightbox}
        >
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white hover:text-gray-300"
          >
            <X className="h-8 w-8" />
          </button>
          <img
            src={lightboxUrl}
            alt="Preview"
            className="max-h-[90vh] max-w-[90vw] object-contain rounded"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}

// --- ArchivoItem sub-component ---
interface ArchivoItemProps {
  archivo: Archivo;
  compact: boolean;
  canDelete: boolean;
  onDescargar: (a: Archivo) => void;
  onEliminar: (a: Archivo) => void;
  onPreview?: () => void;
  showThumbnail: boolean;
}

function ArchivoItem({ archivo, compact, canDelete, onDescargar, onEliminar, onPreview, showThumbnail }: ArchivoItemProps) {
  const [thumbBlobUrl, setThumbBlobUrl] = useState<string | null>(null);
  const [thumbError, setThumbError] = useState(false);

  useEffect(() => {
    if (!showThumbnail || thumbError) return;
    let cancelled = false;

    archivosService.miniatura(archivo.id)
      .then(blob => {
        if (!cancelled) setThumbBlobUrl(URL.createObjectURL(blob));
      })
      .catch(() => {
        if (!cancelled) setThumbError(true);
      });

    return () => { cancelled = true; };
  }, [showThumbnail, thumbError, archivo.id]);

  useEffect(() => {
    return () => {
      if (thumbBlobUrl) URL.revokeObjectURL(thumbBlobUrl);
    };
  }, [thumbBlobUrl]);

  return (
    <div className="flex items-center gap-3 p-2.5 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors group">
      {/* Thumbnail or icon */}
      {thumbBlobUrl ? (
        <button
          onClick={onPreview}
          className="shrink-0 rounded overflow-hidden border hover:ring-2 ring-primary transition-all"
        >
          <img
            src={thumbBlobUrl}
            alt={archivo.nombre_original}
            className={cn('object-cover', compact ? 'h-10 w-10' : 'h-12 w-12')}
          />
        </button>
      ) : (
        <div className="shrink-0" onClick={onPreview} role={onPreview ? 'button' : undefined}>
          {getIcono(archivo.tipo, archivo.mime_type)}
        </div>
      )}

      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{archivo.nombre_original}</p>
        <p className="text-xs text-muted-foreground">
          {archivo.tamanio_humano}
          {archivo.subido_por && ` · ${archivo.subido_por.nombre}`}
          {!compact && ` · ${formatDateTime(archivo.created_at)}`}
        </p>
      </div>

      {/* Actions */}
      <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={() => onDescargar(archivo)}
          title="Descargar"
        >
          <Download className="h-3.5 w-3.5" />
        </Button>
        {canDelete && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-destructive hover:text-destructive"
            onClick={() => onEliminar(archivo)}
            title="Eliminar"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
}
